import * as React from 'react';
import { Icon } from './Icon';

// Hero KPI tile + Sparkline + bars — mirror Docs/Design Handoff/portal/common.jsx.
// The KPI is the backbone of the "simplest format" goal: uppercase label, 32px
// value, optional delta/hint, optional sparkline.

export type DeltaKind = 'up' | 'down' | 'warn' | 'flat';

export interface KpiProps {
  label: string;
  value: React.ReactNode;
  unit?: string;
  delta?: string;
  deltaKind?: DeltaKind;
  spark?: number[];
  accent?: string;
  icon?: string;
  onClick?: () => void;
  hint?: string;
}

export function Kpi({ label, value, unit, delta, deltaKind, spark, accent, icon, onClick, hint }: KpiProps) {
  const [hover, setHover] = React.useState(false);
  const dc = deltaKind === 'down' ? 'var(--status-expired)' : deltaKind === 'warn' ? '#D97706' : 'var(--status-active)';
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => onClick && setHover(false)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px',
        boxShadow: hover ? 'var(--shadow-md)' : 'var(--shadow-card)', transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'box-shadow .15s, transform .15s', cursor: onClick ? 'pointer' : 'default',
        position: 'relative', minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{label}</span>
        {icon && <Icon name={icon} size={15} color={accent || 'var(--text-muted)'} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1, color: 'var(--text-primary)' }}>{value}</span>
        {unit && <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, minHeight: 16 }}>
        {delta ? (
          <span style={{ fontSize: 12, fontWeight: 600, color: dc, display: 'flex', alignItems: 'center', gap: 3 }}>
            {deltaKind !== 'flat' && <Icon name={deltaKind === 'down' ? 'arrow-down-right' : 'arrow-up-right'} size={13} color={dc} />}
            {delta}
          </span>
        ) : hint ? (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>
        ) : (
          <span />
        )}
        {spark && <Sparkline points={spark} color={accent || 'var(--accent)'} />}
      </div>
    </div>
  );
}

export interface SparklineProps {
  points: number[];
  color?: string;
  w?: number;
  h?: number;
}
export function Sparkline({ points, color = 'var(--accent)', w = 72, h = 22 }: SparklineProps) {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - ((p - min) / range) * h).toFixed(1)}`)
    .join(' ');
  const lastY = h - ((points[points.length - 1] - min) / range) * h;
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}

export interface ProgressBarProps {
  pct: number;
  color: string;
  height?: number;
  track?: string;
}
export function ProgressBar({ pct, color, height = 6, track = 'var(--bg-muted)' }: ProgressBarProps) {
  return (
    <div style={{ height, background: track, borderRadius: 99, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 99, transition: 'width .3s' }} />
    </div>
  );
}

// Utilization bar with semantic colour thresholds (<70% accent / 70–90% amber / >=90% red).
export interface UtilBarProps {
  used: number;
  total: number;
  height?: number;
}
export function UtilBar({ used, total, height = 6 }: UtilBarProps) {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const color = pct >= 90 ? 'var(--status-expired)' : pct >= 70 ? '#D97706' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1 }}>
        <ProgressBar pct={pct} color={color} height={height} />
      </div>
      <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, width: 34, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}
